/**
 * AIChatSidebar Component
 * 
 * Chat sidebar for AI-assisted contract editing.
 * Displays conversation history and allows users to request changes.
 */

import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Loader2, CheckCircle2 } from 'lucide-react';
import ApiService from '@/services/api';
import { EndpointKeys } from '@/config/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  changes?: string[];
  timestamp: Date;
}

interface AIChatSidebarProps {
  contractId: string;
  currentHtml: string;
  onAIEdit: (updatedHtml: string, changes: string[]) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
}

export const AIChatSidebar: React.FC<AIChatSidebarProps> = ({
  contractId,
  currentHtml,
  onAIEdit,
  isProcessing,
  setIsProcessing,
}) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: "Hi! I'm here to help you create your nanny employment agreement. What would you like to add or change?",
      timestamp: new Date(),
    }
  ]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isProcessing) return;

    const userMessage = input.trim();
    setInput('');

    // Add user message
    const newUserMessage: Message = {
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newUserMessage]);

    // Set processing state
    setIsProcessing(true);

    try {
      // Call AI API
      const response = await ApiService.request<{
        updated_html: string;
        changes_made: string[];
        ai_message: string;
      }>(EndpointKeys.CONTRACT_BUILDER_AI_EDIT, {
        method: 'POST',
        body: {
          contract_id: contractId,
          user_message: userMessage,
          current_html: currentHtml,
        },
        skipAuth: true,
      });

      // Add AI response
      const aiMessage: Message = {
        role: 'assistant',
        content: response.ai_message,
        changes: response.changes_made,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, aiMessage]);

      // Update contract content
      onAIEdit(response.updated_html, response.changes_made);

    } catch (error) {
      console.error('AI edit error:', error);
      
      // Add error message
      const errorMessage: Message = {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="h-full flex flex-col bg-white border-l border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="font-semibold text-lg">AI Assistant</h3>
        <p className="text-sm text-gray-500">Ask me to modify your contract</p>
      </div>

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div 
            key={idx} 
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div 
              className={`max-w-[85%] p-3 rounded-lg ${
                msg.role === 'user' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-900'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              
              {msg.changes && msg.changes.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-300">
                  <p className="text-xs font-semibold mb-2 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Changes made:
                  </p>
                  <ul className="text-xs space-y-1">
                    {msg.changes.map((change, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <span className="mt-0.5">•</span>
                        <span>{change}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <p className="text-xs mt-2 opacity-70">
                {msg.timestamp.toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </p>
            </div>
          </div>
        ))}
        
        {isProcessing && (
          <div className="flex justify-start">
            <div className="bg-gray-100 text-gray-900 p-3 rounded-lg flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Thinking...</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask AI to make changes..."
            disabled={isProcessing}
            className="flex-1"
          />
          <Button 
            onClick={handleSend} 
            disabled={isProcessing || !input.trim()}
            size="icon"
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        
        <div className="mt-2 text-xs text-gray-500">
          <p>Try: "Add a probation period of 3 months" or "Change the notice period to 2 weeks"</p>
        </div>
      </div>
    </div>
  );
};

